import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Globe,
  MoreHorizontal,
  ThumbsUp,
  MessageCircle,
  Share2,
} from "lucide-react";

/**
 * FacebookCarouselFrame
 * --------------------------------------------------------------
 * A faithful, mobile-first recreation of the Facebook / Meta
 * carousel-ad layout used in the news feed. The "page" surface is
 * intentionally light/white (matching Meta UI) so it pops against
 * the site's dark navy background — the same trick we use for the
 * Instagram Reel mock, just for paid-social.
 *
 *  ┌──────────────────────────────────────┐
 *  │ ⓎCA  Young Chefs Academy        ⋯   │
 *  │      Sponsored · 🌐                   │
 *  ├──────────────────────────────────────┤
 *  │ Post body copy (1–3 lines)           │
 *  ├──────────────────────────────────────┤
 *  │ ┌─────┐ ┌─────┐ ┌─────┐              │
 *  │ │card │ │card │ │card │   ◀ / ▶      │
 *  │ │ ─── │ │ ─── │ │ ─── │              │
 *  │ │ CTA │ │ CTA │ │ CTA │              │
 *  │ └─────┘ └─────┘ └─────┘              │
 *  │ ● ● ● ● ●                            │
 *  ├──────────────────────────────────────┤
 *  │ 👍 Like   💬 Comment   ↗ Share        │
 *  └──────────────────────────────────────┘
 *
 *  Props:
 *    page       — { name, avatar, handle (optional) }
 *    body       — post-body copy shown above the carousel
 *    slides     — [{ src, headline, ctaLabel?, link? }]
 *    testId     — root data-testid
 */
const FacebookCarouselFrame = ({
  page,
  body,
  slides,
  ctaDefault = "Learn More",
  displayUrl,
  testId = "fb-carousel",
}) => {
  const trackRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  // Snap-scroll update — derives the active index from horizontal scroll.
  const onScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const cardW = el.firstElementChild?.clientWidth || 1;
    const gap = 8; // matches gap-2
    const idx = Math.round(el.scrollLeft / (cardW + gap));
    setActiveIdx(Math.max(0, Math.min(slides.length - 1, idx)));
  }, [slides.length]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  const scrollToIdx = (idx) => {
    const el = trackRef.current;
    if (!el) return;
    const cardW = el.firstElementChild?.clientWidth || 1;
    const gap = 8;
    el.scrollTo({ left: idx * (cardW + gap), behavior: "smooth" });
  };

  const canPrev = activeIdx > 0;
  const canNext = activeIdx < slides.length - 1;

  return (
    <div
      className="w-full max-w-[420px] bg-white text-[#050505] rounded-lg overflow-hidden shadow-[0_8px_32px_-12px_rgba(0,0,0,0.55)] font-sans"
      data-testid={testId}
    >
      {/* PAGE HEADER */}
      <div className="px-3 pt-3 pb-2 flex items-start gap-2">
        <div
          className="w-10 h-10 rounded-full bg-[#0a3a5c] flex items-center justify-center text-white font-semibold text-[13px] flex-shrink-0 overflow-hidden"
          aria-label={page.name}
        >
          {page.avatar ? (
            <img
              src={page.avatar}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            page.name.charAt(0)
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold leading-tight truncate">
            {page.name}
          </div>
          <div className="text-[12px] text-[#65676B] flex items-center gap-1 mt-0.5">
            <span>Sponsored</span>
            <span aria-hidden>·</span>
            <Globe size={11} strokeWidth={2} />
          </div>
        </div>
        <button
          type="button"
          className="text-[#65676B] hover:text-[#050505] flex-shrink-0 p-1 -mr-1"
          aria-label="More options"
          tabIndex={-1}
        >
          <MoreHorizontal size={18} />
        </button>
      </div>

      {/* POST BODY */}
      {body && (
        <div className="px-3 pb-3 text-[14px] leading-[1.35] text-[#050505]">
          {body}
        </div>
      )}

      {/* CAROUSEL TRACK */}
      <div className="relative bg-[#f0f2f5] border-y border-[#dadde1]">
        <div
          ref={trackRef}
          className="flex gap-2 overflow-x-auto scroll-smooth snap-x snap-mandatory px-3 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid={`${testId}-track`}
        >
          {slides.map((s, i) => (
            <div
              key={i}
              className="snap-start flex-shrink-0 w-[78%] sm:w-[72%] bg-white rounded-md overflow-hidden border border-[#dadde1] flex flex-col"
              data-testid={`${testId}-slide-${i}`}
            >
              <div className="aspect-square w-full bg-[#f0f2f5]">
                <img
                  src={s.src}
                  alt={s.headline || `Slide ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover block"
                />
              </div>
              <div className="px-3 py-2.5 flex items-center justify-between gap-3 bg-white">
                <div className="min-w-0">
                  {s.headline && (
                    <div className="text-[12px] font-semibold text-[#050505] leading-tight line-clamp-2">
                      {s.headline}
                    </div>
                  )}
                  <div className="text-[11px] text-[#65676B] uppercase tracking-wide mt-0.5">
                    {s.displayUrl || displayUrl || "anneflinn.com"}
                  </div>
                </div>
                <button
                  type="button"
                  className="text-[12px] font-semibold bg-[#E4E6EB] hover:bg-[#d8dadf] text-[#050505] px-3 py-1.5 rounded-md flex-shrink-0 transition-colors"
                  tabIndex={-1}
                >
                  {s.ctaLabel || ctaDefault}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Prev / Next chevrons (desktop) */}
        <button
          type="button"
          onClick={() => canPrev && scrollToIdx(activeIdx - 1)}
          disabled={!canPrev}
          className={`hidden sm:flex absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white shadow-md items-center justify-center transition-opacity ${
            canPrev
              ? "opacity-100 hover:bg-gray-50"
              : "opacity-0 pointer-events-none"
          }`}
          aria-label="Previous slide"
          data-testid={`${testId}-prev-btn`}
        >
          <ChevronLeft size={18} strokeWidth={2.4} className="text-[#050505]" />
        </button>
        <button
          type="button"
          onClick={() => canNext && scrollToIdx(activeIdx + 1)}
          disabled={!canNext}
          className={`hidden sm:flex absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white shadow-md items-center justify-center transition-opacity ${
            canNext
              ? "opacity-100 hover:bg-gray-50"
              : "opacity-0 pointer-events-none"
          }`}
          aria-label="Next slide"
          data-testid={`${testId}-next-btn`}
        >
          <ChevronRight
            size={18}
            strokeWidth={2.4}
            className="text-[#050505]"
          />
        </button>
      </div>

      {/* DOT INDICATORS */}
      <div className="flex items-center justify-center gap-1.5 py-2.5 bg-white">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => scrollToIdx(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${
              i === activeIdx
                ? "w-5 bg-[#1877F2]"
                : "w-1.5 bg-[#bcc0c4] hover:bg-[#65676B]"
            }`}
            data-testid={`${testId}-dot-${i}`}
          />
        ))}
      </div>

      {/* REACTIONS ROW */}
      <div className="grid grid-cols-3 border-t border-[#dadde1] bg-white">
        {[
          { icon: ThumbsUp, label: "Like" },
          { icon: MessageCircle, label: "Comment" },
          { icon: Share2, label: "Share" },
        ].map(({ icon: Icon, label }) => (
          <button
            key={label}
            type="button"
            className="flex items-center justify-center gap-1.5 py-2 text-[13px] font-semibold text-[#65676B] hover:bg-[#f0f2f5] transition-colors"
            tabIndex={-1}
            aria-label={label}
          >
            <Icon size={16} />
            <span>{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default FacebookCarouselFrame;
export { FacebookCarouselFrame };
